import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Text,
  Paper,
  Stack,
  Group,
  Button,
  Select,
  Skeleton,
  SimpleGrid,
  Box,
  Image,
  Badge,
} from '@mantine/core';
import {
  IconArrowLeft,
  IconPrinter,
} from '@tabler/icons-react';
import PageHeader from '@/components/PageHeader';
import EmptyState from '@/components/EmptyState';
import { useProductLabelData } from '@/hooks/usePortalApi';
import api from '@/config/api';

const BARCODE_OPTIONS = [
  { value: 'qr', label: 'QR Code' },
  { value: 'datamatrix', label: 'DataMatrix' },
  { value: 'code128', label: 'Code 128' },
  { value: 'gs1_128', label: 'GS1-128' },
];

export default function LabelDetail() {
  const { id } = useParams<{ id: string }>();
  const productId = parseInt(id || '0', 10);
  const [barcodeType, setBarcodeType] = useState('qr');
  const { data: label, isLoading } = useProductLabelData(productId, {
    barcode_type: barcodeType,
  });

  const handlePrint = () => {
    const baseUrl = api.defaults.baseURL || '';
    const url = `${baseUrl}/catalog/products/${productId}/label?barcode_type=${barcodeType}`;
    window.open(url, '_blank');
  };

  if (isLoading) {
    return (
      <Stack gap="lg">
        <Skeleton height={40} width={300} />
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} height={80} />
          ))}
        </SimpleGrid>
        <Skeleton height={300} />
      </Stack>
    );
  }

  if (!label) {
    return (
      <EmptyState
        title="Product not found"
        description="The product you're looking for doesn't exist or you don't have access."
        action={
          <Button component={Link} to="/labels">
            Back to Labels
          </Button>
        }
      />
    );
  }

  return (
    <Stack gap="lg">
      <Group>
        <Button
          component={Link}
          to="/labels"
          variant="subtle"
          size="sm"
          leftSection={<IconArrowLeft size={16} />}
        >
          Labels
        </Button>
      </Group>

      <PageHeader
        title={`Product Label - ${label.product_name}`}
        description={`SKU: ${label.sku}`}
        actions={
          <Group gap="sm">
            <Select
              data={BARCODE_OPTIONS}
              value={barcodeType}
              onChange={(v) => setBarcodeType(v ?? 'qr')}
              size="sm"
              w={160}
            />
            <Button
              leftSection={<IconPrinter size={16} />}
              onClick={handlePrint}
            >
              Print Label
            </Button>
          </Group>
        }
      />

      {/* Label Preview */}
      <Paper p="xl" withBorder style={{ maxWidth: 480 }}>
        <Stack gap="md">
          {/* Product name */}
          <Group gap="sm">
            <Text size="xl" fw={700} c="#2d6a4f">
              {label.product_name}
            </Text>
            {label.is_organic && (
              <Badge color="green" size="sm" variant="filled">ORGANIC</Badge>
            )}
          </Group>

          {/* SKU / GTIN */}
          <Text size="sm" c="dimmed">
            SKU: {label.sku} &nbsp;|&nbsp; GTIN: {label.gtin}
          </Text>

          {/* Barcode image */}
          <Box ta="center" py="sm">
            <Image
              src={`data:image/png;base64,${label.barcode_image}`}
              alt={`${barcodeType} barcode`}
              maw={200}
              mx="auto"
            />
          </Box>

          {/* GS1 data string */}
          {label.gs1_data_string && (
            <Text size="xs" c="dimmed" ta="center" style={{ wordBreak: 'break-all' }}>
              <Text span fw={700}>GS1: </Text>{label.gs1_data_string}
            </Text>
          )}

          {/* Best Before */}
          {label.best_before && (
            <Paper
              p="sm"
              ta="center"
              withBorder
              style={{ borderColor: '#d32f2f', borderWidth: 2 }}
            >
              <Text size="xs" fw={700} c="red">BEST BEFORE</Text>
              <Text size="lg" fw={700} c="red">{label.best_before}</Text>
            </Paper>
          )}

          {/* Info grid */}
          <SimpleGrid cols={2} spacing="xs">
            {label.pack_date && (
              <>
                <Text size="sm" c="dimmed">Pack Date:</Text>
                <Text size="sm" fw={600}>{label.pack_date}</Text>
              </>
            )}
            {label.country_of_origin && (
              <>
                <Text size="sm" c="dimmed">Origin:</Text>
                <Text size="sm" fw={600}>{label.country_of_origin}</Text>
              </>
            )}
            {label.net_weight && (
              <>
                <Text size="sm" c="dimmed">Net Weight:</Text>
                <Text size="sm" fw={600}>{label.net_weight}</Text>
              </>
            )}
            <Text size="sm" c="dimmed">Lot:</Text>
            <Text size="sm" fw={600}>{label.lot_number}</Text>
          </SimpleGrid>

          {/* Allergens */}
          {label.allergens.length > 0 && (
            <Paper p="sm" bg="orange.0" withBorder style={{ borderColor: '#ff9800' }}>
              <Text size="sm" fw={700}>
                CONTAINS: {label.allergens.join(', ')}
              </Text>
            </Paper>
          )}

          {/* Storage */}
          {label.storage_zone && (
            <Text size="xs" c="dimmed">
              <Text span fw={700} tt="uppercase">{label.storage_zone}</Text>
              {' '}&mdash; {label.storage_instruction}
            </Text>
          )}
        </Stack>
      </Paper>
    </Stack>
  );
}
